import { BAGS_TO_BLOCKS, MAX_CAPACITY_BLOCKS } from '../types/index';
import { list as listInventory } from './inventoryService';
import { list as listRefills, totalConsumedBlocks as getTotalConsumedBlocks } from './refillService';
import { latest as getLatestSaltLevel } from './saltLevelService';
import { buildInventoryTimeline, binTimeline, toDate, } from '../utils/chart';
// ---------------------------------------------------------------------------
// Forecast computation (pure functions)
// ---------------------------------------------------------------------------
function daysBetween(a, b) {
    const da = toDate(a);
    const db = toDate(b);
    const ms = Math.abs(db.getTime() - da.getTime());
    return Math.max(1, Math.round(ms / (1000 * 60 * 60 * 24)));
}
function sortByDateAsc(events) {
    return [...events].sort((e1, e2) => {
        if (e1.date === e2.date)
            return e1.createdAt < e2.createdAt ? -1 : 1;
        return e1.date < e2.date ? -1 : 1;
    });
}
function computeAverages(events) {
    if (!events || events.length < 3)
        return null;
    const sorted = sortByDateAsc(events);
    const intervals = [];
    for (let i = 1; i < sorted.length; i++) {
        intervals.push(daysBetween(sorted[i - 1].date, sorted[i].date));
    }
    const avgIntervalDays = intervals.reduce((a, b) => a + b, 0) / intervals.length;
    const avgBlocksPerRefill = sorted.reduce((sum, e) => sum + (Number.isFinite(e.blocks) ? e.blocks : 0), 0) / sorted.length;
    const lastRefillDate = sorted[sorted.length - 1].date;
    if (!Number.isFinite(avgIntervalDays) || avgIntervalDays <= 0 || !Number.isFinite(avgBlocksPerRefill) || avgBlocksPerRefill <= 0) {
        return null;
    }
    return { avgIntervalDays, avgBlocksPerRefill, lastRefillDate };
}
function computeDailyRate(avgBlocksPerRefill, avgIntervalDays) {
    const rate = avgBlocksPerRefill / avgIntervalDays;
    return rate > 0 ? rate : 0;
}
function addDays(baseISO, days) {
    const dt = toDate(baseISO);
    dt.setDate(dt.getDate() + Math.round(days));
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const d = String(dt.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}
function getNextRefillDate(lastRefillISO, avgIntervalDays) {
    return addDays(lastRefillISO, avgIntervalDays);
}
function getDepletionDate(availableBlocks, dailyRate, asOfISO) {
    if (!Number.isFinite(availableBlocks) || availableBlocks <= 0 || !Number.isFinite(dailyRate) || dailyRate <= 0)
        return undefined;
    const base = asOfISO ?? new Date().toISOString().slice(0, 10);
    const daysRemaining = Math.floor(availableBlocks / dailyRate);
    return addDays(base, daysRemaining);
}
function forecastFromHistory(events, availableBlocks, asOfISO, interimReading) {
    const updatedAt = new Date().toISOString();
    const today = asOfISO ?? new Date().toISOString().slice(0, 10);
    const averages = computeAverages(events);
    if (!averages) {
        return {
            insufficientData: true,
            message: 'Forecasts requires more history',
            updatedAt,
        };
    }
    let dailyRate = computeDailyRate(averages.avgBlocksPerRefill, averages.avgIntervalDays);
    let effectiveAvgInterval = averages.avgIntervalDays;
    const sortedEvents = sortByDateAsc(events);
    const lastRefill = sortedEvents[sortedEvents.length - 1];
    /**
     * Interim reading blending algorithm:
     * When a user reports a salt level reading after the last refill, we blend the
     * observed consumption rate with the historical average to improve forecast accuracy.
     *
     * - Observed rate: (blocksConsumed since refill) / (days since refill)
     * - Blended rate: 60% observed + 40% historical
     *
     * The 60/40 weighting favors recent observation while smoothing outliers.
     * This adjusts both the next-refill prediction and estimated current level.
     */
    if (interimReading && interimReading.percentage >= 0 && interimReading.percentage <= 100) {
        const daysSinceRefill = daysBetween(lastRefill.date, interimReading.date);
        if (daysSinceRefill >= 1) {
            const blocksAtReading = (interimReading.percentage / 100) * MAX_CAPACITY_BLOCKS;
            const blocksConsumed = lastRefill.blocks - blocksAtReading;
            if (blocksConsumed > 0) {
                const observedRate = blocksConsumed / daysSinceRefill;
                dailyRate = observedRate * 0.6 + dailyRate * 0.4;
                if (dailyRate > 0) {
                    effectiveAvgInterval = averages.avgBlocksPerRefill / dailyRate;
                }
            }
        }
    }
    const nextRefillDate = getNextRefillDate(averages.lastRefillDate, effectiveAvgInterval);
    const depletionDate = getDepletionDate(Math.max(0, availableBlocks), dailyRate, asOfISO);
    // Compute estimated current salt level percentage
    let estimatedLevelPct;
    if (dailyRate > 0) {
        // If we have an interim reading more recent than the last refill, start from there
        if (interimReading && interimReading.date >= lastRefill.date) {
            const daysSinceReading = daysBetween(interimReading.date, today);
            const blocksAtReading = (interimReading.percentage / 100) * MAX_CAPACITY_BLOCKS;
            const blocksRemaining = blocksAtReading - (daysSinceReading * dailyRate);
            estimatedLevelPct = Math.max(0, Math.min(100, (blocksRemaining / MAX_CAPACITY_BLOCKS) * 100));
        }
        else {
            // Start from last refill
            const daysSinceLastRefill = daysBetween(lastRefill.date, today);
            const blocksRemaining = lastRefill.blocks - (daysSinceLastRefill * dailyRate);
            estimatedLevelPct = Math.max(0, Math.min(100, (blocksRemaining / MAX_CAPACITY_BLOCKS) * 100));
        }
    }
    return {
        insufficientData: false,
        avgIntervalDays: averages.avgIntervalDays,
        avgBlocksPerRefill: averages.avgBlocksPerRefill,
        dailyRate,
        lastRefillDate: averages.lastRefillDate,
        nextRefillDate,
        depletionDate,
        estimatedLevelPct,
        updatedAt,
    };
}
// ---------------------------------------------------------------------------
// Public insight endpoints
// ---------------------------------------------------------------------------
export async function getForecast() {
    const [items, events, latestSaltLevel] = await Promise.all([
        listInventory(),
        listRefills(),
        getLatestSaltLevel(),
    ]);
    const arrivedBags = items
        .filter(i => i.status === 'arrived')
        .reduce((sum, i) => sum + i.quantityBags, 0);
    const blocksArrived = arrivedBags * BAGS_TO_BLOCKS;
    const totalConsumed = events.reduce((sum, e) => sum + (Number.isFinite(e.blocks) ? e.blocks : 0), 0);
    const availableBlocks = Math.max(0, blocksArrived - totalConsumed);
    const interimReading = latestSaltLevel
        ? { percentage: latestSaltLevel.percentage, date: latestSaltLevel.date }
        : undefined;
    return forecastFromHistory(events, availableBlocks, undefined, interimReading);
}
export async function getInventoryStats() {
    const [items, totalConsumed] = await Promise.all([
        listInventory(),
        getTotalConsumedBlocks(),
    ]);
    const arrivedBags = items
        .filter(i => i.status === 'arrived')
        .reduce((sum, i) => sum + i.quantityBags, 0);
    const pendingBags = items
        .filter(i => i.status === 'ordered')
        .reduce((sum, i) => sum + i.quantityBags, 0);
    const totalBagsOrdered = items.reduce((sum, i) => sum + i.quantityBags, 0);
    const blocksArrived = arrivedBags * BAGS_TO_BLOCKS;
    const availableBlocks = Math.max(0, blocksArrived - totalConsumed);
    // Last ordered item (most recent by dateAdded — items already sorted descending)
    const lastOrdered = items.length > 0 ? { dateAdded: items[0].dateAdded } : undefined;
    // Average order frequency: mean interval (days) between dateAdded across all items
    let avgOrderFrequencyDays;
    if (items.length >= 2) {
        const sorted = [...items].sort((a, b) => (a.dateAdded < b.dateAdded ? -1 : 1));
        let totalDays = 0;
        let count = 0;
        for (let i = 1; i < sorted.length; i++) {
            const prev = new Date(sorted[i - 1].dateAdded);
            const curr = new Date(sorted[i].dateAdded);
            const diff = Math.round(Math.abs(curr.getTime() - prev.getTime()) / (1000 * 60 * 60 * 24));
            if (diff > 0) {
                totalDays += diff;
                count++;
            }
        }
        if (count > 0) {
            avgOrderFrequencyDays = Math.round(totalDays / count);
        }
    }
    return {
        totals: { bagsArrived: arrivedBags, blocksArrived, totalBagsOrdered, pendingBags },
        availableBlocks,
        lastOrdered,
        avgOrderFrequencyDays,
    };
}
export async function getRefillStats() {
    const [events, forecast] = await Promise.all([
        listRefills(),
        getForecast(),
    ]);
    const totalBlocksConsumed = events.reduce((sum, e) => sum + (Number.isFinite(e.blocks) ? e.blocks : 0), 0);
    // Most recent refill (events sorted newest-first by listRefills)
    const lastRefill = events.length > 0 ? events[0] : undefined;
    // Average days per bag: (avgIntervalDays * BAGS_TO_BLOCKS) / avgBlocksPerRefill
    let avgDaysPerBag;
    if (!forecast.insufficientData &&
        forecast.avgIntervalDays &&
        forecast.avgBlocksPerRefill) {
        avgDaysPerBag = Math.round((forecast.avgIntervalDays * BAGS_TO_BLOCKS) / forecast.avgBlocksPerRefill);
    }
    return { totalBlocksConsumed, lastRefill, avgDaysPerBag };
}
export async function getChartData(bin) {
    const [items, events, forecast] = await Promise.all([
        listInventory(),
        listRefills(),
        getForecast(),
    ]);
    // Build base timeline and bin it
    const timeline = buildInventoryTimeline(items, events);
    const binned = binTimeline(timeline, bin);
    let labels = binned.labels;
    let inventoryData = binned.inventory;
    let refillData = binned.refills;
    let forecastData = [];
    // Extend with forecast depletion endpoint
    const lastInv = inventoryData.length > 0 ? inventoryData[inventoryData.length - 1] : 0;
    if (!forecast.insufficientData && forecast.depletionDate) {
        if (labels[labels.length - 1] !== forecast.depletionDate) {
            labels = [...labels, forecast.depletionDate];
            inventoryData = [...inventoryData, lastInv];
            refillData = [...refillData, 0];
        }
        forecastData = new Array(labels.length).fill(null);
        if (labels.length >= 2) {
            forecastData[labels.length - 2] = lastInv;
            forecastData[labels.length - 1] = 0;
        }
    }
    // Salt level data no longer shown on chart — return empty array for backward compatibility
    const saltLevelData = new Array(labels.length).fill(null);
    return { labels, inventoryData, refillData, forecastData, saltLevelData };
}
